import { Plus, TrashSimple } from '@phosphor-icons/react'
import { Button, Form, InputNumber, Select, Switch } from 'antd'
import { useMemo } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { Skeleton } from '@/components/Skeleton'
import { CONDITION_LIST } from '@/domain/constants/conditions'
import { useGetPacketOrForm } from '@/hooks/useGetPacketOrForm'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import useSearchParams from '@/hooks/useSearchParams'
import { useLoadQuestionByResourceId } from '@/queries/questions/useLoadQuestionByResourceId'
import FormAntd from '@/ui/form/antd/FormAntd'
import { getUniqueResourceNames } from '@/utils/conditionalCompletePage/getUniqueResourceNames'
import { getFilterOption } from '@/utils/getFilterOption'
import cn from '@/utils/tailwind/cn'

import { CustomPageEditor } from '../CustomPageEditor'
import { ConditionRenderFields } from './ConditionRenderFields'
import {
  FormAddConditionCompletePageModel,
  useAddConditionCompletePageForm,
} from './useAddConditionCompletePageForm'

export const COMPLETE_PAGE_ACTIONS = [
  {
    label: 'Show Custom Page',
    value: 'custom_page',
  },
  {
    label: 'Redirect to URL',
    value: 'redirect_url',
  },
]

export type ConditionCompletePageFormProps = {
  initialValues?: FormAddConditionCompletePageModel
  onFinish: (values: FormAddConditionCompletePageModel) => void
}
export const ConditionCompletePageForm = ({
  initialValues,
  onFinish,
}: ConditionCompletePageFormProps) => {
  const searchParams = useSearchParams()
  const updateParams = useUpdateURLSearchParams()
  const onCancel = () =>
    updateParams(
      ['condition_settings', undefined],
      ['edit_condition_settings', undefined],
      ['form_id', undefined]
    )

  const DEFAULT_FORM_ADD_CONDITION_COMPLETE_PAGE = {
    id: uuidv4(),
    is_new: true,
    enabled: true,
    order: null,
    conditional_rule_groups: [
      {
        conditional_rules: [
          {
            type: 'AND',
            question: undefined,
            operator: undefined,
            expected_answer: undefined,
          },
        ],
      },
    ],
  }

  const formId = searchParams.get('form_id')

  const { form: resourceFormId, packet } = useGetPacketOrForm()
  const { data: questionsData = [], isLoading } = useLoadQuestionByResourceId({
    form: resourceFormId,
    packet,
    with_choices: true,
  })

  const hasInitialValues = Object.keys(initialValues || {}).length

  let buttonSubmitText = 'Add'
  if (hasInitialValues) buttonSubmitText = 'Save'

  const { form, rule } = useAddConditionCompletePageForm()

  const formName = useMemo(() => {
    if (!packet) return
    return getUniqueResourceNames(questionsData).map((item) => ({
      label: item,
      value: uuidv4(),
    }))
  }, [packet, questionsData])

  const filteredQuestions = useMemo(() => {
    if (formId) {
      const findFormById = formName?.find((item) => item.value === formId)

      return questionsData?.filter(
        (question) =>
          (['signature', 'radio', 'checkbox', 'datetime', 'number'].includes(
            question?.type
          ) ||
            [
              'full_name',
              'name',
              'phone',
              'email',
              'single_line',
              'multi_line',
              'calculation',
            ].includes(question?.subtype)) &&
          question.resource_name === findFormById?.label
      )
    }

    return questionsData?.filter(
      (question) =>
        ['signature', 'radio', 'checkbox', 'datetime', 'number'].includes(
          question?.type
        ) ||
        [
          'full_name',
          'name',
          'phone',
          'email',
          'single_line',
          'multi_line',
          'calculation',
        ].includes(question?.subtype)
    )
  }, [formId, formName, questionsData])

  const questionChoices = useMemo(() => {
    return filteredQuestions.map((question, index) => ({
      label: `${index + 1}. ${question.text || 'Untitled'}`,
      value: String(question.id),
    }))
  }, [filteredQuestions])

  const conditions = (selectedQuestionId: string) => {
    const selectedQuestion = filteredQuestions.find(
      (item) => String(item.id) === selectedQuestionId
    )
    if (!selectedQuestion) return []

    if (
      [
        'full_name',
        'name',
        'phone',
        'email',
        'single_line',
        'multi_line',
        'datetime',
        'time_only',
      ].includes(selectedQuestion?.subtype) ||
      ['signature'].includes(selectedQuestion?.type)
    ) {
      return CONDITION_LIST?.filter?.(
        (condition) =>
          condition.value === 'IS_FILLED' || condition.value === 'IS_EMPTY'
      )
    }

    if (['date_only', 'month_year'].includes(selectedQuestion?.subtype)) {
      return CONDITION_LIST?.filter?.((condition) =>
        ['IS_FILLED', 'IS_EMPTY', 'IS_BEFORE', 'IS_AFTER'].includes(
          condition.value
        )
      )
    }

    if (
      selectedQuestion?.type === 'number' ||
      selectedQuestion?.subtype === 'calculation'
    ) {
      return CONDITION_LIST?.filter?.((condition) =>
        [
          'EQUALS',
          'NOT_EQUALS',
          'GREATER_THAN',
          'LESS_THAN',
          'IS_FILLED',
          'IS_EMPTY',
        ].includes(condition.value)
      )
    }

    if (selectedQuestion?.type === 'checkbox') {
      return CONDITION_LIST?.filter?.((condition) => {
        return [
          'EQUALS',
          'NOT_EQUALS',
          'IS_FILLED',
          'IS_EMPTY',
          'CONTAINS',
          'NOT_CONTAINS',
        ].includes(condition.value)
      })
    }

    if (selectedQuestion?.type === 'radio') {
      return CONDITION_LIST?.filter?.((condition) =>
        ['EQUALS', 'NOT_EQUALS', 'IS_FILLED', 'IS_EMPTY'].includes(
          condition.value
        )
      )
    }

    return []
  }

  const handleOperatorChange = (
    groupIndex: number,
    ruleIndex: number,
    newType: string
  ) => {
    const currentValues = form.getFieldsValue()
    const newRuleGroups = [...currentValues.conditional_rule_groups]

    if (newType === 'OR') {
      const currentGroup = newRuleGroups[groupIndex]

      const rulesBefore = currentGroup.conditional_rules.slice(0, ruleIndex)
      const rulesAfter = currentGroup.conditional_rules.slice(ruleIndex)

      newRuleGroups[groupIndex] = {
        ...currentGroup,
        conditional_rules: rulesBefore,
      }

      newRuleGroups.splice(groupIndex + 1, 0, {
        conditional_rules: rulesAfter.map((rule, idx) => ({
          ...rule,
          type: !idx ? 'OR' : 'AND',
        })),
      })

      const nonEmptyGroups = newRuleGroups.filter(
        (group) => group.conditional_rules.length > 0
      )

      if (nonEmptyGroups.length !== newRuleGroups.length) {
        form.setFieldsValue({ conditional_rule_groups: nonEmptyGroups })
        return
      }
    } else if (newType === 'AND' && groupIndex > 0) {
      const previousGroup = newRuleGroups[groupIndex - 1]
      const currentGroup = newRuleGroups[groupIndex]

      newRuleGroups[groupIndex - 1] = {
        ...previousGroup,
        conditional_rules: [
          ...previousGroup.conditional_rules,
          ...currentGroup.conditional_rules,
        ],
      }

      newRuleGroups.splice(groupIndex, 1)
    }

    form.setFieldsValue({ conditional_rule_groups: newRuleGroups })
  }

  const handleQuestionOrOperatorChange = (
    groupIndex: number,
    ruleIndex: number,
    fieldName: 'question' | 'operator',
    value: string
  ) => {
    form.setFieldsValue({
      conditional_rule_groups: form
        .getFieldsValue()
        .conditional_rule_groups.map((group, gIdx) => ({
          ...group,
          conditional_rules: group.conditional_rules.map((rule, rIdx) => {
            if (gIdx === groupIndex && rIdx === ruleIndex) {
              return {
                ...rule,
                [fieldName]: value,
                expected_answer: undefined,
              }
            }
            return rule
          }),
        })),
    })

    if (fieldName === 'operator' && (value === 'AND' || value === 'OR')) {
      handleOperatorChange(groupIndex, ruleIndex, value)
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {packet ? (
        <Skeleton className="!h-8" {...{ isLoading }}>
          <div className={cn('-mb-2 mt-3 w-2/3', { 'mb-1': isLoading })}>
            <Select
              className="w-full"
              onChange={(value) => updateParams(['form_id', value])}
              options={formName}
              placeholder="Select a form to filter questions"
            />
          </div>
        </Skeleton>
      ) : null}
      <Skeleton className="!h-[170px]" {...{ isLoading }}>
        <FormAntd.Root<FormAddConditionCompletePageModel>
          className="relative flex h-full w-full"
          form={form}
          initialValues={
            initialValues || DEFAULT_FORM_ADD_CONDITION_COMPLETE_PAGE
          }
          layout="vertical"
          onFinish={onFinish}
          validateTrigger={['onBlur']}
        >
          <Form.Item<FormAddConditionCompletePageModel>
            noStyle
            name="id"
            rules={[rule]}
          >
            <FormAntd.Input className="hidden" />
          </Form.Item>
          <Form.Item<FormAddConditionCompletePageModel>
            noStyle
            name="form"
            rules={[rule]}
          >
            <FormAntd.Input className="hidden" />
          </Form.Item>
          <Form.Item<FormAddConditionCompletePageModel>
            noStyle
            name="packet"
            rules={[rule]}
          >
            <FormAntd.Input className="hidden" />
          </Form.Item>
          <Form.Item<FormAddConditionCompletePageModel>
            noStyle
            name="enabled"
            rules={[rule]}
          >
            <Switch className="hidden" />
          </Form.Item>
          <Form.Item<FormAddConditionCompletePageModel>
            noStyle
            name="is_new"
            rules={[rule]}
          >
            <Switch className="hidden" />
          </Form.Item>
          <Form.Item<FormAddConditionCompletePageModel>
            noStyle
            name="order"
            rules={[rule]}
          >
            <InputNumber className="hidden" />
          </Form.Item>
          <div className="mb-4 mt-8 rounded border !border-gray-200/70 bg-gray-50 py-7 pl-4 pr-2 [&_.group-fields:last-child]:mb-0">
            <Form.List name="conditional_rule_groups">
              {(groups, { remove: removeGroup }) => (
                <>
                  {groups.map((groupField, groupIndex) => (
                    <div className="group-fields mb-2" key={groupField.key}>
                      <Form.List
                        {...groupField}
                        name={[groupField.name, 'conditional_rules']}
                      >
                        {(fields, { add, remove }) => (
                          <>
                            <div className="w-full [&_.conditional-rule-item:last-child_.conditional-rule-item-type]:before:hidden">
                              {fields.map((field, ruleIndex) => (
                                <div
                                  className="conditional-rule-item mb-4 flex w-full items-center"
                                  key={field.key}
                                >
                                  <div className="grid w-full grid-cols-[72px_minmax(0,1fr)_145px_minmax(0,1fr)_50px] gap-x-3">
                                    {ruleIndex === 0 && groupIndex === 0 ? (
                                      <span className="flex h-full w-full items-center justify-center px-1 text-xs font-bold text-gray-600">
                                        IF
                                      </span>
                                    ) : (
                                      <div
                                        className={cn(
                                          "conditional-rule-item-type relative h-full w-full before:absolute before:-bottom-2/3 before:left-1/2 before:h-7 before:w-0.5 before:rounded-xl before:bg-gray-300 before:content-['']",
                                          ruleIndex === 0 && 'before:left-1/2'
                                        )}
                                      >
                                        <Form.Item<
                                          FormAddConditionCompletePageModel['conditional_rule_groups'][0]['conditional_rules']
                                        >
                                          required
                                          className="mb-0 w-full"
                                          name={[field.name, 'type']}
                                          rules={[rule]}
                                        >
                                          <Select
                                            className="w-full"
                                            onChange={(value) =>
                                              handleOperatorChange(
                                                groupIndex,
                                                ruleIndex,
                                                value as string
                                              )
                                            }
                                            options={['AND', 'OR'].map(
                                              (value) => ({
                                                label: value,
                                                value,
                                              })
                                            )}
                                            placeholder="Type"
                                          />
                                        </Form.Item>
                                      </div>
                                    )}
                                    <Form.Item<
                                      FormAddConditionCompletePageModel['conditional_rule_groups'][0]['conditional_rules']
                                    >
                                      required
                                      className="mb-0 w-full"
                                      name={[field.name, 'question']}
                                      rules={[rule]}
                                    >
                                      <Select
                                        showSearch
                                        filterOption={(input, option) =>
                                          getFilterOption(input, option)
                                        }
                                        onChange={(value) =>
                                          handleQuestionOrOperatorChange(
                                            groupIndex,
                                            ruleIndex,
                                            'question',
                                            String(value)
                                          )
                                        }
                                        options={questionChoices}
                                        placeholder="Question"
                                      />
                                    </Form.Item>
                                    <Form.Item<FormAddConditionCompletePageModel>
                                      noStyle
                                      shouldUpdate={(
                                        prevValues,
                                        currentValues
                                      ) =>
                                        prevValues.conditional_rule_groups?.[
                                          groupIndex
                                        ]?.conditional_rules?.[field.name]
                                          ?.question !==
                                        currentValues
                                          ?.conditional_rule_groups?.[
                                          groupIndex
                                        ]?.conditional_rules?.[field.name]
                                          ?.question
                                      }
                                    >
                                      {({ getFieldValue }) => {
                                        const selectedQuestion = (
                                          getFieldValue([
                                            'conditional_rule_groups',
                                            groupIndex,
                                            'conditional_rules',
                                            field.name,
                                          ]) as FormAddConditionCompletePageModel['conditional_rule_groups'][0]['conditional_rules'][0]
                                        ).question

                                        return (
                                          <Form.Item<
                                            FormAddConditionCompletePageModel['conditional_rule_groups'][0]['conditional_rules']
                                          >
                                            required
                                            className="mb-0 w-full"
                                            name={[field.name, 'operator']}
                                            rules={[rule]}
                                          >
                                            <Select
                                              onChange={(value) =>
                                                handleQuestionOrOperatorChange(
                                                  groupIndex,
                                                  ruleIndex,
                                                  'operator',
                                                  String(value)
                                                )
                                              }
                                              options={conditions(
                                                selectedQuestion
                                              )}
                                              placeholder="Condition"
                                            />
                                          </Form.Item>
                                        )
                                      }}
                                    </Form.Item>
                                    <Form.Item<FormAddConditionCompletePageModel>
                                      noStyle
                                      shouldUpdate={(
                                        prevValues,
                                        currentValues
                                      ) =>
                                        prevValues.conditional_rule_groups?.[
                                          groupIndex
                                        ]?.conditional_rules?.[field.name]
                                          ?.operator !==
                                          currentValues
                                            ?.conditional_rule_groups?.[
                                            groupIndex
                                          ]?.conditional_rules?.[field.name]
                                            ?.operator ||
                                        prevValues.conditional_rule_groups?.[
                                          groupIndex
                                        ]?.conditional_rules?.[field.name]
                                          ?.question !==
                                          currentValues
                                            ?.conditional_rule_groups?.[
                                            groupIndex
                                          ]?.conditional_rules?.[field.name]
                                            ?.question
                                      }
                                    >
                                      {({ getFieldValue }) => {
                                        const selectedOperator = (
                                          getFieldValue([
                                            'conditional_rule_groups',
                                            groupIndex,
                                            'conditional_rules',
                                            field.name,
                                          ]) as FormAddConditionCompletePageModel['conditional_rule_groups'][0]['conditional_rules'][0]
                                        ).operator

                                        const selectedQuestionId = (
                                          getFieldValue([
                                            'conditional_rule_groups',
                                            groupIndex,
                                            'conditional_rules',
                                            field.name,
                                          ]) as FormAddConditionCompletePageModel['conditional_rule_groups'][0]['conditional_rules'][0]
                                        ).question

                                        return (
                                          <Form.Item
                                            required
                                            className="mb-0 w-full"
                                            name={[
                                              field.name,
                                              'expected_answer',
                                            ]}
                                            rules={[rule]}
                                          >
                                            <ConditionRenderFields
                                              {...{
                                                questions: questionsData,
                                                selectedOperator,
                                                selectedQuestionId,
                                                name: [
                                                  field.name,
                                                  'expected_answer',
                                                ],
                                              }}
                                            />
                                          </Form.Item>
                                        )
                                      }}
                                    </Form.Item>
                                    {ruleIndex || groupIndex ? (
                                      <Button
                                        className="h-full w-full pl-1 pr-2 focus:!outline-none focus-visible:!outline-none active:outline-none"
                                        onClick={() => {
                                          if (ruleIndex === 0) {
                                            return removeGroup(groupIndex)
                                          }
                                          remove(ruleIndex)
                                        }}
                                        type="link"
                                      >
                                        <TrashSimple className="text-2xl text-gray-800 hover:text-primary" />
                                      </Button>
                                    ) : (
                                      <i />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {groupIndex === groups.length - 1 && (
                              <div className="w-full">
                                <Button
                                  className="border-primary bg-gray-50 text-sm text-primary hover:!bg-primary/5"
                                  onClick={() =>
                                    add(
                                      DEFAULT_FORM_ADD_CONDITION_COMPLETE_PAGE
                                        .conditional_rule_groups[0]
                                        .conditional_rules[0]
                                    )
                                  }
                                >
                                  <Plus weight="bold" /> Add Condition
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </Form.List>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </div>
          <div className="mb-8 mt-2 flex w-full flex-col rounded border !border-gray-200/70 bg-gray-50 py-7 pl-4 pr-2">
            <Form.Item<FormAddConditionCompletePageModel>
              className="mb-0 w-full"
              label="If condition is met"
              name="if_met"
              rules={[rule]}
            >
              <Select
                className="mb-2 w-full"
                options={COMPLETE_PAGE_ACTIONS}
                placeholder="Select Action"
              />
            </Form.Item>

            <Form.Item<FormAddConditionCompletePageModel>
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.if_met !== currentValues?.if_met
              }
            >
              {({ getFieldValue }) => {
                const isRedirectUrl = getFieldValue('if_met') === 'redirect_url'
                const isCustomPage = getFieldValue('if_met') === 'custom_page'

                if (isRedirectUrl) {
                  return (
                    <Form.Item<FormAddConditionCompletePageModel>
                      className="mb-0 w-full"
                      label="Enter URL"
                      name="url"
                      rules={[rule]}
                    >
                      <FormAntd.Input
                        className="bg-white"
                        placeholder="https://"
                      />
                    </Form.Item>
                  )
                }

                if (isCustomPage) {
                  return (
                    <Form.Item<FormAddConditionCompletePageModel>
                      className="mb-0 w-full"
                      name="complete_page"
                      rules={[rule]}
                    >
                      <CustomPageEditor />
                    </Form.Item>
                  )
                }
              }}
            </Form.Item>
          </div>
          <div className="flex w-full justify-end gap-4">
            <Button onClick={onCancel} type="default">
              Cancel
            </Button>
            <Button className="mr-6" htmlType="submit" type="primary">
              {buttonSubmitText}
            </Button>
          </div>
        </FormAntd.Root>
      </Skeleton>
      <Skeleton className="mt-2 !h-32" {...{ isLoading }}>
        <i />
      </Skeleton>
    </div>
  )
}
